import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import {
  CreateDbOntologyTablePayload,
  DbOntologyTable,
  useCreateDbOntologyTable,
  useUpdateDbOntologyNode,
} from "@/service-hooks/use-get-db-ontology-service";

interface DbOntologyTableDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectUuid: string;
  dataLakeId: string;
  onCreated?: () => void;
  // the table to edit; create mode when absent
  table?: DbOntologyTable | null;
  isReadOnly?: boolean;
}

const EMPTY: Omit<CreateDbOntologyTablePayload, "projectUuid" | "dataLakeId"> = {
  name: "",
  schema: "",
  tableType: "table",
  comment: "",
  ddlText: "",
};

export default function DbOntologyTableDialog({
  isOpen,
  onClose,
  projectUuid,
  dataLakeId,
  onCreated,
  table,
  isReadOnly = false,
}: DbOntologyTableDialogProps) {
  const [form, setForm] = useState({ ...EMPTY });
  const createTable = useCreateDbOntologyTable();
  const updateNode = useUpdateDbOntologyNode();
  const isEdit = !!table?.id;
  const isPending = createTable.isPending || updateNode.isPending;

  useEffect(() => {
    if (isOpen) {
      setForm(table ? { ...EMPTY, name: table.name ?? "", comment: table.description ?? "" } : { ...EMPTY });
    }
  }, [isOpen, table]);

  const update = (key: keyof typeof EMPTY, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || isReadOnly) return;
    try {
      if (isEdit && table?.id) {
        // the backend cascades a rename onto the table's columns, constraints and indexes
        await updateNode.mutateAsync({
          label: "DDLTable",
          projectUuid,
          id: table.id,
          dataLakeId,
          data: { name: form.name.trim(), comment: form.comment?.trim() ?? "" },
        });
        onClose();
        return;
      }
      await createTable.mutateAsync({
        projectUuid,
        dataLakeId,
        name: form.name.trim(),
        schema: form.schema?.trim() || undefined,
        tableType: form.tableType?.trim() || "table",
        comment: form.comment?.trim() || undefined,
        ddlText: form.ddlText?.trim() || undefined,
      });
      onCreated?.();
      onClose();
    } catch {
      // toast handled in mutation
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{isReadOnly ? "View Table" : isEdit ? "Edit Table" : "Create Table"}</DialogTitle>
          <DialogDescription className="sr-only">
            Configure the table fields for the selected data lake.
          </DialogDescription>
        </DialogHeader>

        <div className={`space-y-4 overflow-y-auto flex-1 pr-1 ${CUSTOM_SCROLLBAR}`}>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">
              Name <span className="text-error">*</span>
            </label>
            <Input
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Enter table name"
              readOnly={isReadOnly}
              className="bg-surface text-white"
            />
          </div>

          {!isEdit && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">Schema</label>
              <Input
                value={form.schema || ""}
                onChange={(e) => update("schema", e.target.value)}
                placeholder="e.g. public"
                className="bg-surface text-white"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">Table Type</label>
              <Input
                value={form.tableType || ""}
                onChange={(e) => update("tableType", e.target.value)}
                placeholder="table"
                className="bg-surface text-white"
              />
            </div>
          </div>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Comment</label>
            <Textarea
              value={form.comment || ""}
              onChange={(e) => update("comment", e.target.value)}
              placeholder="Optional description for this table"
              readOnly={isReadOnly}
              className="bg-surface text-white min-h-[60px]"
            />
          </div>

          {!isEdit && (
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">DDL Text</label>
            <Textarea
              value={form.ddlText || ""}
              onChange={(e) => update("ddlText", e.target.value)}
              placeholder="CREATE TABLE ..."
              className="bg-surface text-white min-h-[120px] font-mono text-xs"
            />
          </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-surface-border">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {isReadOnly ? "Close" : "Cancel"}
          </Button>
          {!isReadOnly && (
            <Button
              onClick={handleSubmit}
              disabled={!form.name.trim() || isPending}
              className="bg-primary hover:bg-primary-dark text-white"
            >
              {isPending ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save" : "Create"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
